/**
 * Hook for tool settings with 30-minute cache and stale-while-revalidate pattern.
 * Shows cached data immediately, revalidates in background.
 */

import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchToolSettings, updateToolSetting, ToolSetting } from '@/services/toolSettings';
import { toast } from 'sonner';

export const TOOL_SETTINGS_KEY = ['tool-settings'];

// Cache TTL: 2 minutes (reduced for faster admin changes propagation)
const STALE_TIME_MS = 2 * 60 * 1000;

// Keep in cache for 10 minutes
const GC_TIME_MS = 10 * 60 * 1000;

export function useToolSettings(options?: { enabled?: boolean }) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: TOOL_SETTINGS_KEY,
    queryFn: fetchToolSettings,
    staleTime: STALE_TIME_MS,
    gcTime: GC_TIME_MS,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: options?.enabled ?? true,
    // Keep previous data while fetching new data (prevents flicker)
    placeholderData: (previousData) => previousData,
  });

  const settings = query.data ?? [];

  const getSettingByToolId = useCallback((toolId: string): ToolSetting | undefined => {
    return settings.find(s => s.tool_id === toolId);
  }, [settings]);

  const updateSetting = useCallback(async (
    toolId: string,
    updates: Partial<Pick<ToolSetting, 'is_enabled' | 'allowed_roles' | 'disabled_message' | 'background_image_url'>>
  ) => {
    try {
      await updateToolSetting(toolId, updates);
      await queryClient.invalidateQueries({ queryKey: TOOL_SETTINGS_KEY });
      toast.success('Configuração atualizada com sucesso');
    } catch (err) {
      console.error('Error updating tool setting:', err);
      toast.error('Erro ao atualizar configuração');
      throw err;
    }
  }, [queryClient]);

  const toggleToolEnabled = useCallback(async (toolId: string) => {
    const setting = getSettingByToolId(toolId);
    if (setting) {
      await updateSetting(toolId, { is_enabled: !setting.is_enabled });
    }
  }, [getSettingByToolId, updateSetting]);

  const updateAllowedRoles = useCallback(async (toolId: string, roles: string[]) => {
    await updateSetting(toolId, { allowed_roles: roles });
  }, [updateSetting]);

  // Initial loading = no data yet AND actively fetching
  // This distinguishes from background revalidation (which has cached data)
  const isInitialLoading = query.isLoading && query.data === undefined;

  return {
    settings,
    // CRITICAL: 'loading' should be true only on initial load (no cache)
    // This prevents flicker when revalidating in background
    loading: isInitialLoading,
    isRefetching: query.isFetching && !!query.data,
    error: query.error,
    getSettingByToolId,
    updateSetting,
    toggleToolEnabled,
    updateAllowedRoles,
    refetch: query.refetch,
  };
}
